// client/src/components/pages/RouteIntelligence/useMonitorRuns.ts (ST-00229, audit ANL-15)
//
// Manual monitor runs as background jobs, shared by the Variance, Preload and Receipt Recon
// dashboards. A manual run backfills the whole lookback window (about 15–30 s per monitor), so
// "Run All" took minutes and outlived the client's 30 s timeout: the page said "Failed" while the
// server kept working, and a second click started a duplicate backfill. Now:
//   POST …/run/:id   → 202 { jobId, monitorIds, skipped }   (409 when that monitor is already running)
//   POST …/run-all   → 202 { jobId, monitorIds, skipped }
//   GET  …/run-jobs/:jobId every RUN_POLL_MS until status 'done' → refresh + summary toast.
// Polling stops when the dashboard unmounts and gives up after RUN_GIVE_UP_MS (the run carries
// on on the server). A response without a jobId (a server from before ST-00229, mid-deploy) is
// treated as the finished result.
import { useCallback, useEffect, useRef, useState } from 'react';
import type { AxiosResponse } from 'axios';
import type {
  MonitorApiError,
  MonitorRunJob,
  MonitorRunJobStart,
  MonitorRunOutcome,
} from '../../../types/clientPages/routeIntelligence';

export const RUN_POLL_MS = 2000;
export const RUN_GIVE_UP_MS = 15 * 60 * 1000;

export type RunToastType = 'success' | 'warning' | 'error';

/** The run endpoints of one api namespace (routeIntelligence / preloadEvaluation / receiptRecon). */
export interface MonitorRunApi {
  runMonitor: (monitorId: number) => Promise<AxiosResponse<MonitorRunJobStart>>;
  runAll: () => Promise<AxiosResponse<MonitorRunJobStart>>;
  getRunJob: (jobId: string) => Promise<AxiosResponse<MonitorRunJob>>;
}

interface RunnableMonitor {
  monitor_id: number;
  grouping_name?: string;
}

interface UseMonitorRunsOptions<M extends RunnableMonitor> {
  api: MonitorRunApi;
  showToast: (message: string, type?: RunToastType) => void;
  /** Reloads the dashboard (silently) once a run has finished. */
  refresh: () => void;
  /** The toast for one finished monitor run (the dashboard's own wording). */
  describeResult: (
    monitor: M,
    result: MonitorRunOutcome
  ) => { message: string; type: RunToastType };
}

type Settled = MonitorRunJob | 'gave_up' | 'lost' | null;

const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? '' : 's'}`;

/** A legacy synchronous run response (no jobId): the run-all `{ results }` or one monitor's result. */
const legacyJob = (data: unknown): MonitorRunJob => {
  const body = (data ?? {}) as { results?: MonitorRunOutcome[] } & MonitorRunOutcome;
  const results = Array.isArray(body.results) ? body.results : [body];
  return {
    jobId: '',
    status: 'done',
    total: results.length,
    completed: results.length,
    results,
  };
};

export function useMonitorRuns<M extends RunnableMonitor>({
  api,
  showToast,
  refresh,
  describeResult,
}: UseMonitorRunsOptions<M>) {
  const [runningIds, setRunningIds] = useState<Set<number>>(() => new Set());
  const alive = useRef(true);
  const timers = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  useEffect(() => {
    alive.current = true;
    const pending = timers.current;
    return () => {
      alive.current = false;
      for (const t of pending) clearTimeout(t);
      pending.clear();
    };
  }, []);

  const addRunning = useCallback((ids: number[]) => {
    setRunningIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) next.add(id);
      return next;
    });
  }, []);

  const removeRunning = useCallback((ids: number[]) => {
    if (!alive.current) return;
    setRunningIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) next.delete(id);
      return next;
    });
  }, []);

  /** Resolves after `ms`; never resolves once the dashboard has unmounted (the timer is cleared). */
  const wait = useCallback(
    (ms: number) =>
      new Promise<void>((resolve) => {
        const t = setTimeout(() => {
          timers.current.delete(t);
          resolve();
        }, ms);
        timers.current.add(t);
      }),
    []
  );

  /** Polls the job until it is done; 'lost' when the server no longer knows it (404). */
  const settle = useCallback(
    async (start: MonitorRunJobStart | undefined): Promise<Settled> => {
      if (!start || !start.jobId) return legacyJob(start);
      const deadline = Date.now() + RUN_GIVE_UP_MS;
      while (alive.current) {
        await wait(RUN_POLL_MS);
        if (!alive.current) return null;
        try {
          const { data } = await api.getRunJob(start.jobId);
          if (!alive.current) return null;
          if (data && data.status === 'done') return data;
        } catch (err) {
          if ((err as MonitorApiError).response?.status === 404) return 'lost';
          // A dropped poll is retried on the next tick (the run itself is unaffected).
        }
        if (Date.now() >= deadline) return 'gave_up';
      }
      return null;
    },
    [api, wait]
  );

  const runOne = useCallback(
    async (monitor: M) => {
      const id = monitor.monitor_id;
      const name = monitor.grouping_name || `Monitor ${id}`;
      addRunning([id]);
      try {
        const res = await api.runMonitor(id);
        if (!alive.current) return;
        if (res.data?.jobId) showToast(`Run started: ${name}`);
        const job = await settle(res.data);
        if (job === null) return;
        if (job === 'gave_up') {
          showToast(
            `${name} is still running on the server. Refresh later to see the results.`,
            'warning'
          );
          return;
        }
        if (job === 'lost') {
          showToast(
            'The run status is no longer available. Refresh to see the results.',
            'warning'
          );
          refresh();
          return;
        }
        const result = job.results[0];
        if (result) {
          const { message, type } = describeResult(monitor, result);
          showToast(message, type);
        }
        refresh();
      } catch (err) {
        if (!alive.current) return;
        const e = err as MonitorApiError;
        if (e.response?.status === 409) {
          showToast(`${name} is already running`, 'warning');
        } else {
          showToast(`Failed: ${name}`, 'error');
        }
      } finally {
        removeRunning([id]);
      }
    },
    [api, addRunning, removeRunning, settle, showToast, refresh, describeResult]
  );

  const runAll = useCallback(
    async (activeIds: number[]) => {
      let ids = activeIds;
      addRunning(ids);
      try {
        const res = await api.runAll();
        if (!alive.current) return;
        const start = res.data;
        if (start?.jobId) {
          // Show exactly the monitors this job runs (the skipped ones run elsewhere).
          removeRunning(ids.filter((id) => !start.monitorIds.includes(id)));
          addRunning(start.monitorIds);
          ids = Array.from(new Set([...ids, ...start.monitorIds]));
          showToast(`Run started: ${plural(start.monitorIds.length, 'monitor')}`);
        }
        const job = await settle(start);
        if (job === null) return;
        if (job === 'gave_up') {
          showToast(
            'The monitors are still running on the server. Refresh later to see the results.',
            'warning'
          );
          return;
        }
        if (job === 'lost') {
          showToast(
            'The run status is no longer available. Refresh to see the results.',
            'warning'
          );
          refresh();
          return;
        }
        const failed = job.results.filter((r) => !r.success).length;
        const skipped = start?.jobId ? start.skipped.length : 0;
        let message = `Processed ${plural(job.results.length, 'monitor')}`;
        if (failed) message += `, ${failed} failed`;
        if (skipped) message += `, ${skipped} already running`;
        showToast(message, failed || skipped ? 'warning' : 'success');
        refresh();
      } catch {
        if (!alive.current) return;
        showToast('Failed to run monitors', 'error');
      } finally {
        removeRunning(ids);
      }
    },
    [api, addRunning, removeRunning, settle, showToast, refresh]
  );

  return { runningIds, runOne, runAll };
}

/** The toast for a failed monitor run (shared by the three dashboards). */
export const describeFailure = (
  name: string | undefined,
  result: MonitorRunOutcome
): { message: string; type: RunToastType } => ({
  message: `${name ?? 'Monitor'}: ${result.error || result.reason || 'the run failed'}`,
  type: 'error',
});
